// eslint-disable-next-line no-unused-vars
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';

// Hooks
import { useFocusOutside } from '../../../../common/hooks/useFocusOutside';

// Enums
import { KEY_CODES } from '../../../../common/enums/keycodes';

const FilterPrefixOptionsMenu = styled.div`
    background: #fff;
    position: absolute;
    right: 0;
    top: 100%;
    margin-top: 4px;
    width: 100%;
    box-shadow: 0px 3px 4px 1px #00000054;
    z-index: 100;
`;

const FilterPrefixOptions = styled.ul`
    position: relative;
    display: block;
    clear: both;
    list-style-type: none;
    width: 100%;
    padding: 0;
    margin: 0;
`;

const FilterPrefixOptionBase = `
    position: relative;
    display: block;
    clear: both;
    width: 100%;
    padding: 0.667rem 0.84rem;
    background-color: #fff;
    border: none;
`;

const FilterPrefixOptionEl = styled.li`
    ${FilterPrefixOptionBase};
    transition: background-color 0.2s ease;
    cursor: pointer;
    border-bottom: 1px solid #f5f5f5;
    text-align: left;
    &:hover {
        background-color: #f8faff;
    }
    ${({ focused }) => focused && css`
        border: 1px solid #333;
    `}
`;

const FilterPrefixNoOptionsEl = styled.li`
    ${FilterPrefixOptionBase};
    text-align: center;
`;

const HIJACKED_KEY_CODES = Object.values(KEY_CODES).reduce((acc, curr) => Array.isArray(curr) ? [...acc, ...curr] : [...acc, curr], []);

export const DataFilterByColumnMenu = ({
    id,
    onSelect,
    containerRef,
    filterableColumns,
    setShowFilterColumnOptions,
    ...props
}) => {
    const [cursor, setCursor] = useState(0);
    const optionsCount = useMemo(() => filterableColumns.length - 1, [filterableColumns]);
    const clickOutsideCallback = () => {
        setShowFilterColumnOptions(false);
    };
    useFocusOutside(containerRef, clickOutsideCallback);
    const keyboardNavigationHandler = useCallback((e) => {
        if (HIJACKED_KEY_CODES.includes(e.keyCode)) {
            e.preventDefault();
            e.stopPropagation();

            switch (e.keyCode) {
                case KEY_CODES.UP_ARROW:
                    setCursor(cursor === 0 ? optionsCount : cursor - 1);
                    break;
                case KEY_CODES.DOWN_ARROW:
                    setCursor(cursor === optionsCount ? 0 : cursor + 1);
                    break;
                case KEY_CODES.SPACEBAR[0]:
                case KEY_CODES.SPACEBAR[1]:
                case KEY_CODES.ENTER:
                    onSelect(filterableColumns[cursor]);
                    break;
                case KEY_CODES.ESCAPE:
                    setShowFilterColumnOptions(false);
                    break;
            }
        }
    }, [filterableColumns, cursor, setCursor]);

    useEffect(() => {
        document.addEventListener('keydown', keyboardNavigationHandler);

        return () => {
            document.removeEventListener('keydown', keyboardNavigationHandler);
        };
    }, [keyboardNavigationHandler]);

    return (
        <FilterPrefixOptionsMenu>
            <FilterPrefixOptions {...{ id }}>
                {filterableColumns.length
                    ? filterableColumns.map((column, index) => (
                        <FilterPrefixOptionEl
                            focused={cursor === index}
                            key={column.Header}
                            id={`columnFilterOption_${column.Header}`}
                            position={index}
                            onClick={() => onSelect(column)}
                            defaultActive={index === 0}
                            tabindex="0"
                            role="button"
                        >
                            <small>Search by:</small> {column.Header}
                        </FilterPrefixOptionEl>
                    ))
                    : <FilterPrefixNoOptionsEl>No matching filterable columns</FilterPrefixNoOptionsEl>
                }
            </FilterPrefixOptions>
        </FilterPrefixOptionsMenu>
    );
};

DataFilterByColumnMenu.propTypes = {
    id: PropTypes.string.isRequired,
    onSelect: PropTypes.func,
    setShowFilterColumnOptions: PropTypes.func,
    containerRef: PropTypes.oneOfType([
        // Either a function
        PropTypes.func,
        // Or the instance of a DOM native element (see the note about SSR)
        PropTypes.shape({ current: PropTypes.instanceOf(Element) })
    ]),
    filterableColumns: PropTypes.arrayOf(PropTypes.object)
};

DataFilterByColumnMenu.defaultProps = {
    onSelect: () => {},
    setShowFilterColumnOptions: () => {},
    filterableColumns: []
};
